import { useEffect, useCallback, useRef, useState } from "react";
import { useWebSocket } from "@/services/websocket";
import { useCollaborationStore } from "@/stores/collaboration";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useCollaborationEditorBridge } from "@/stores/editor/collaboration-editor-bridge";
import { socketClient } from "@/services/websocket/socketClient";
import { config } from "@/config";
import { ComponentType, OperationType } from "@/types";
import type { ComponentInput, Operation, Position } from "@/types";
import {
  UserSession,
  DragConflict,
  CollaborationState as StoreCollaborationState,
} from "@/stores/collaboration/collaboration-store";
import { logger } from "@/utils";

// ─── Stat snapshot ───────────────────────────────────────────────────────────

export type CollaborationStats = StoreCollaborationState["stats"];

// ─── Detailed status (from getCollaborationStatus) ───────────────────────────

export interface CollaborationStatus {
  isActive: boolean;
  connectionState: string;
  operationsPending: number;
  lastSync: Date | null;
}

// ─── State slice ─────────────────────────────────────────────────────────────

export interface CollaborationState {
  isActive: boolean;
  isConnected: boolean;
  activeUsers: UserSession[];
  currentUser: UserSession | null;
  conflicts: DragConflict[];
  hasActiveConflicts: boolean;
  stats: CollaborationStats;
}

// ─── Actions slice ───────────────────────────────────────────────────────────

export interface CollaborationActions {
  // Lifecycle (both hooks)
  startCollaboration: (projectId: string) => Promise<void>;
  stopCollaboration: () => Promise<void>;

  // Reconnect (from useIntegratedCollaboration)
  reconnect: () => Promise<void>;

  // Operations with optimistic updates (from useCollaboration)
  sendOperation: (operation: Operation, optimistic?: boolean) => Promise<void>;

  // Cursor updates:
  //   updateCursor    — throttled (60ms), from original useCollaboration
  //   sendCursorUpdate — direct pass-through to bridge, from useIntegratedCollaboration
  // Unified hook exposes both names; updateCursor is the preferred call site.
  updateCursor: (position: Position) => void;
  sendCursorUpdate: (position: Position) => void;

  // Selection (from useCollaboration)
  updateSelection: (componentIds: string[]) => void;

  // Conflict resolution (from useCollaboration)
  resolveConflict: (
    conflictId: string,
    resolution: "merge" | "reject" | "queue",
  ) => void;

  // Sync (both hooks)
  requestSync: () => void;

  // Status query (from useIntegratedCollaboration)
  getCollaborationStatus: () => CollaborationStatus;
}

// ─── Full return type ────────────────────────────────────────────────────────

export type UseCollaborationReturn = CollaborationState & CollaborationActions;

// ─── Deterministic user color ────────────────────────────────────────────────

const generateUserColor = (userId: string): string => {
  const colors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E9",
  ];

  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isComponentType = (value: unknown): value is ComponentType =>
  typeof value === "string" &&
  Object.values(ComponentType).some((componentType) => componentType === value);

const parsePosition = (value: unknown): Position | null => {
  if (
    !isRecord(value) ||
    !isFiniteNumber(value.x) ||
    !isFiniteNumber(value.y)
  ) {
    return null;
  }

  return {
    x: value.x,
    y: value.y,
    z: isFiniteNumber(value.z) ? value.z : 0,
  };
};

const parseComponentInput = (value: unknown): ComponentInput | null => {
  if (
    !isRecord(value) ||
    typeof value.name !== "string" ||
    !isComponentType(value.type)
  ) {
    return null;
  }

  const properties = value.properties;
  if (properties !== undefined && !isRecord(properties)) {
    return null;
  }

  const pageId = value.pageId;
  if (pageId !== undefined && typeof pageId !== "string") {
    return null;
  }

  const component: ComponentInput = {
    name: value.name,
    type: value.type,
    pageId: pageId ?? "",
    properties: properties ?? {},
  };

  if (typeof value.id === "string") {
    component.id = value.id;
  }

  if (typeof value.projectId === "string") {
    component.projectId = value.projectId;
  }

  if (typeof value.parentId === "string") {
    component.parentId = value.parentId;
  }

  if (isRecord(value.style)) {
    component.style = value.style;
  }

  if (
    Array.isArray(value.children) &&
    value.children.every((child) => typeof child === "string")
  ) {
    component.children = value.children;
  }

  const position = parsePosition(value.position);
  if (position) {
    component.position = position;
  }

  if (isRecord(value.size)) {
    const { width, height } = value.size;
    if (
      (typeof width === "number" || typeof width === "string") &&
      (typeof height === "number" || typeof height === "string")
    ) {
      component.size = { width, height };
    }
  }

  if (typeof value.order === "number") {
    component.order = value.order;
  }

  if (typeof value.locked === "boolean") {
    component.locked = value.locked;
  }

  if (typeof value.hidden === "boolean") {
    component.hidden = value.hidden;
  }

  return component;
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useCollaboration = (): UseCollaborationReturn => {
  const { user, tokens } = useAuthStore();
  const bridge = useCollaborationEditorBridge();
  const collaboration = useCollaborationStore();
  const {
    isConnected: wsIsConnected,
    isInProject,
    currentProjectId,
    sendOperation: wsSendOperation,
    sendCursorUpdate: wsSendCursorUpdate,
    sendSelectionUpdate,
    sendPresenceUpdate,
    requestSync: wsRequestSync,
    onProjectStateReceived,
    onUserJoined,
    onUserLeft,
    onOperationReceived,
    onOperationAck,
    onCursorUpdate,
    onSelectionUpdate,
    onPresenceUpdate,
  } = useWebSocket();

  const {
    sessionId,
    projectId: collabProjectId,
    isActive,
    currentUser,
    activeUsers,
    dragConflicts,
    hasActiveConflicts,
    stats,
    initializeSession,
    terminateSession,
    addUser,
    removeUser,
    updateUserCursor,
    updateUserSelection,
    updateUserAction,
    updateUserPresence,
    createDragConflict,
    resolveDragConflict,
    addPendingOperation,
    acknowledgeOperation,
    failOperation,
    incrementVectorClock,
    updateLastSyncTime,
  } = collaboration;

  const {
    components,
    selectedComponents,
    updateComponent,
    addComponent,
    deleteComponent,
  } = useEditorStore();

  // ─── Optimistic updates state (from original useCollaboration) ───────────

  const [optimisticOperations, setOptimisticOperations] = useState<
    Map<string, Operation>
  >(new Map());
  const operationTimeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const socketEventUnsubscribers = useRef<Array<() => void>>([]);

  // ─── Throttled cursor updates (from original useCollaboration) ───────────

  const lastCursorUpdate = useRef<number>(0);
  const cursorUpdateQueue = useRef<Position | null>(null);
  const cursorUpdateTimer = useRef<NodeJS.Timeout | null>(null);

  // ─── Circuit-breaker refs (from useIntegratedCollaboration) ──────────────

  const connectionRetryCount = useRef(0);
  const maxRetries = 3;
  const retryDelay = 1000;
  const isRetrying = useRef(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isWebSocketUnavailable = useRef(false);
  const lastFailureTime = useRef<number>(0);
  const isJoining = useRef(false);
  const currentJoinProjectId = useRef<string | null>(null);
  const lastJoinAttempt = useRef<number>(0);
  const bridgeActiveRef = useRef(bridge.isCollaborationActive);
  const JOIN_COOLDOWN = 2000;

  useEffect(() => {
    bridgeActiveRef.current = bridge.isCollaborationActive;
  }, [bridge.isCollaborationActive]);

  // ─── Start collaboration (integrated implementation) ─────────────────────

  const startCollaboration = useCallback(
    async (projectId: string): Promise<void> => {
      // Feature-flag guard
      if (!config.features.realTimeCollaboration) {
        logger.info("Real-time collaboration is disabled", { projectId });
        return;
      }

      if (!user || !tokens) {
        throw new Error("User must be authenticated to start collaboration");
      }

      // Enhanced duplicate prevention with cooldown
      const now = Date.now();
      const timeSinceLastAttempt = now - lastJoinAttempt.current;

      // Prevent concurrent join attempts for the same project
      if (isJoining.current && currentJoinProjectId.current === projectId) {
        logger.warn("Already joining project, skipping duplicate request", {
          projectId,
          timeSinceLastAttempt,
        });
        return;
      }

      // Prevent rapid successive join attempts (debounce)
      if (
        currentJoinProjectId.current === projectId &&
        timeSinceLastAttempt < JOIN_COOLDOWN
      ) {
        logger.warn("Join attempt too soon, enforcing cooldown", {
          projectId,
          timeSinceLastAttempt,
          cooldownRemaining: JOIN_COOLDOWN - timeSinceLastAttempt,
        });
        return;
      }

      // If joining a different project, wait for current join to complete
      if (isJoining.current && currentJoinProjectId.current !== projectId) {
        logger.info(
          "Waiting for current join to complete before joining new project",
          {
            current: currentJoinProjectId.current,
            new: projectId,
          },
        );
        await new Promise((resolve) => setTimeout(resolve, 1000));
        if (isJoining.current) {
          throw new Error(
            "Another project join operation is still in progress",
          );
        }
      }

      // Mark join attempt with timestamp
      isJoining.current = true;
      currentJoinProjectId.current = projectId;
      lastJoinAttempt.current = now;

      // Check if WebSocket is already connected
      if (socketClient.isConnected()) {
        logger.info("WebSocket already connected, resetting circuit breaker", {
          projectId,
          userId: user.id,
        });
        isWebSocketUnavailable.current = false;
        connectionRetryCount.current = 0;
        lastFailureTime.current = 0;
      }

      // Circuit breaker pattern — check if WebSocket is marked unavailable
      if (isWebSocketUnavailable.current) {
        const timeSinceLastFailure = Date.now() - lastFailureTime.current;
        const cooldownPeriod = 300000; // 5 minutes

        if (timeSinceLastFailure < cooldownPeriod) {
          logger.warn(
            "WebSocket marked as unavailable, skipping collaboration start",
            {
              projectId,
              cooldownRemaining: cooldownPeriod - timeSinceLastFailure,
            },
          );
          throw new Error("WebSocket service is temporarily unavailable");
        } else {
          isWebSocketUnavailable.current = false;
          connectionRetryCount.current = 0;
        }
      }

      try {
        logger.info("Starting collaboration", {
          projectId,
          userId: user.id,
        });

        // Step 1: Connect to WebSocket if not connected
        if (!socketClient.isConnected()) {
          logger.info("WebSocket not connected, attempting connection", {
            projectId,
            userId: user.id,
          });
          await socketClient.connect({
            url: config.websocket.url,
            token: tokens.accessToken,
            userId: user.id,
            reconnectionAttempts: 2,
            reconnectionDelay: 1000,
          });
        } else {
          logger.info(
            "WebSocket already connected, proceeding to join project",
            {
              projectId,
              userId: user.id,
            },
          );
        }

        // Step 2: Join project room
        await socketClient.joinProject(projectId);

        // Step 3: Initialize collaboration bridge
        await bridge.initializeCollaboration(projectId);

        // Step 4: Initialize collaboration session in the store
        const collabSessionId = `session_${projectId}_${Date.now()}`;
        initializeSession(collabSessionId, projectId, user);

        // Step 5: Set up comprehensive WebSocket event handlers via socketClient
        socketEventUnsubscribers.current.forEach((unsubscribe) =>
          unsubscribe(),
        );
        socketEventUnsubscribers.current = [];

        socketEventUnsubscribers.current = [
          socketClient.on("operationAck", (data) => {
            bridge.acknowledgeOperation(
              data.operationId,
              data.transformedOperation,
            );
            collaboration.updateVectorClock(data.vectorClock);
          }),

          socketClient.on("error", (error) => {
            // The SocketClientEvents interface types this as Error, but the server
            // may send structured errors with additional fields if the socket layer
            // is updated to preserve them. Use narrowing to check safely.
            if (
              typeof error === "object" &&
              error !== null &&
              "type" in error &&
              isRecord(error) &&
              error.type === "operation-failed"
            ) {
              if (
                typeof error.operationId === "string" &&
                typeof error.message === "string"
              ) {
                bridge.handleOperationFailure(error.operationId, error.message);
              }
            }
          }),

          socketClient.on("operationReceived", (data) => {
            logger.debug("Received remote operation", {
              operationId: data.payload.operation.id,
              type: data.payload.operation.type,
              componentId: data.payload.operation.componentId,
              userId: data.userId,
            });
            bridge.handleRemoteOperation(data.payload.operation);
            collaboration.incrementOperationsReceived();
          }),

          socketClient.on("cursorUpdate", (data) => {
            const { userId, payload } = data;
            if (userId !== user.id) {
              collaboration.updateUserCursor(userId, payload.position);
            }
          }),

          socketClient.on("selectionUpdate", (data) => {
            const { userId, payload } = data;
            if (userId !== user.id) {
              collaboration.updateUserSelection(userId, payload.selection);
            }
          }),

          socketClient.on("presenceUpdate", (data) => {
            const { userId, payload } = data;
            if (userId !== user.id) {
              collaboration.updateUserPresence(
                userId,
                payload.action === "connect" ? "online" : "offline",
              );
            }
          }),

          socketClient.on("userJoined", (data) => {
            const { userId, user: userData } = data;
            if (userId !== user.id) {
              collaboration.addUser({
                userId,
                user: userData,
                currentAction: "idle",
                color: generateUserColor(userId),
                lastActivity: new Date(),
                isOnline: true,
              });
            }
          }),

          socketClient.on("userLeft", (data) => {
            const { userId } = data;
            if (userId !== user.id) {
              collaboration.removeUser(userId);
            }
          }),

          socketClient.on("projectStateReceived", (data) => {
            logger.info("Received project state", {
              componentsCount: data.components?.length,
              participantsCount: data.session?.participants?.length,
            });

            const editorStore = useEditorStore.getState();
            if (data.components) {
              editorStore.setComponents(data.components);
            }

            collaboration.updateVectorClock(data.vectorClock || {});
            collaboration.updateLastSyncTime();
          }),
        ];

        // Step 6: Request initial project state sync
        socketClient.requestSync();

        connectionRetryCount.current = 0;
        isJoining.current = false;
        // Keep currentJoinProjectId set to prevent immediate re-attempts
        logger.info("Collaboration started successfully", {
          projectId,
        });
      } catch (error) {
        isJoining.current = false;
        currentJoinProjectId.current = null;
        logger.error("Failed to start collaboration", {
          projectId,
          error,
        });
        lastFailureTime.current = Date.now();

        // Check if this is a non-retryable error
        const errorMessage = error instanceof Error ? error.message : "";
        const isDatabaseError =
          errorMessage.includes("E11000 duplicate key error") ||
          errorMessage.includes("operations.id_1 dup key") ||
          errorMessage.includes("MongoError") ||
          errorMessage.includes("ValidationError");
        const isProjectNotFoundError =
          errorMessage.includes("Project not found");

        if (isDatabaseError || isProjectNotFoundError) {
          logger.error(
            "Non-retryable error detected - marking WebSocket as unavailable",
            {
              isDatabaseError,
              isProjectNotFoundError,
              projectId,
              error: errorMessage,
            },
          );
          isWebSocketUnavailable.current = true;
          lastFailureTime.current = Date.now();
          connectionRetryCount.current = maxRetries;
          throw error;
        }

        // Retry logic with circuit breaker (only for retryable errors)
        if (connectionRetryCount.current < maxRetries && !isRetrying.current) {
          connectionRetryCount.current++;
          isRetrying.current = true;

          logger.info(
            `Retrying collaboration connection (${connectionRetryCount.current}/${maxRetries})`,
          );

          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }

          retryTimeoutRef.current = setTimeout(() => {
            if (isRetrying.current) {
              isRetrying.current = false;
              startCollaboration(projectId).catch((retryError) => {
                logger.error("Retry attempt failed", {
                  projectId,
                  retryError,
                });
                isRetrying.current = false;
              });
            }
          }, retryDelay * connectionRetryCount.current);
        } else {
          isWebSocketUnavailable.current = true;
          isRetrying.current = false;
          connectionRetryCount.current = 0;

          logger.error(
            "Max retry attempts reached - WebSocket marked as unavailable",
            {
              projectId,
              attempts: maxRetries,
              cooldownPeriod: "5 minutes",
            },
          );

          logger.info(
            "Collaboration disabled - continuing in single-user mode",
            { projectId },
          );
          return;
        }
      }
    },
    [user, tokens, bridge, initializeSession, collaboration],
  );

  // ─── Stop collaboration (integrated implementation) ──────────────────────

  const stopCollaboration = useCallback(async (): Promise<void> => {
    try {
      logger.info("Stopping collaboration");

      // Reset joining state
      isJoining.current = false;
      currentJoinProjectId.current = null;
      lastJoinAttempt.current = 0;

      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      // Leave project room
      if (socketClient.isConnected()) {
        await socketClient.leaveProject();
      }

      // Terminate collaboration bridge
      await bridge.terminateCollaboration();

      // Clean up optimistic operations
      operationTimeoutRefs.current.forEach((timeoutRef) => {
        clearTimeout(timeoutRef);
      });
      setOptimisticOperations(new Map());
      operationTimeoutRefs.current.clear();

      // Terminate collaboration session in the store
      terminateSession();

      // Clean up WebSocket handlers
      socketEventUnsubscribers.current.forEach((unsubscribe) => unsubscribe());
      socketEventUnsubscribers.current = [];

      // Reset retry state
      connectionRetryCount.current = 0;
      isRetrying.current = false;
      isWebSocketUnavailable.current = false;
      lastFailureTime.current = 0;

      logger.info("Collaboration stopped successfully");
    } catch (error) {
      logger.error("Error stopping collaboration", error);
      // Don't throw — allow cleanup to continue
    }
  }, [bridge, terminateSession]);

  // ─── Reconnect (from useIntegratedCollaboration) ─────────────────────────

  const reconnect = useCallback(async (): Promise<void> => {
    if (!bridge.currentProjectId) {
      logger.warn("Cannot reconnect: no active project");
      return;
    }

    try {
      logger.info("Attempting to reconnect collaboration");
      await startCollaboration(bridge.currentProjectId);
    } catch (error) {
      logger.error("Failed to reconnect collaboration", error);
      throw error;
    }
  }, [bridge.currentProjectId, startCollaboration]);

  // ─── Throttled cursor update (from original useCollaboration) ────────────

  const updateCursor = useCallback(
    (position: Position) => {
      if (!isActive) return;

      const now = Date.now();
      const timeSinceLastUpdate = now - lastCursorUpdate.current;
      const throttleDelay = 60; // 60ms for ~16fps

      cursorUpdateQueue.current = position;

      if (timeSinceLastUpdate >= throttleDelay) {
        wsSendCursorUpdate(position, false);
        lastCursorUpdate.current = now;
        cursorUpdateQueue.current = null;
      } else {
        if (cursorUpdateTimer.current) {
          clearTimeout(cursorUpdateTimer.current);
        }

        cursorUpdateTimer.current = setTimeout(() => {
          if (cursorUpdateQueue.current) {
            wsSendCursorUpdate(cursorUpdateQueue.current, false);
            lastCursorUpdate.current = Date.now();
            cursorUpdateQueue.current = null;
          }
        }, throttleDelay - timeSinceLastUpdate);
      }
    },
    [isActive, wsSendCursorUpdate],
  );

  // ─── Direct cursor update (from useIntegratedCollaboration) ──────────────
  // Unthrottled pass-through for callers that manage their own throttling.

  const sendCursorUpdateDirect = useCallback(
    (position: Position) => {
      if (!bridge.isCollaborationActive) return;
      bridge.sendCursorUpdate(position);
    },
    [bridge],
  );

  // ─── Update selection (from original useCollaboration) ───────────────────

  const updateSelection = useCallback(
    (componentIds: string[]) => {
      if (!isActive) return;

      sendSelectionUpdate(componentIds);

      if (user) {
        updateUserSelection(user.id, componentIds);
      }
    },
    [isActive, sendSelectionUpdate, updateUserSelection, user],
  );

  // ─── Resolve conflict (from original useCollaboration) ───────────────────

  const resolveConflict = useCallback(
    (conflictId: string, resolution: "merge" | "reject" | "queue") => {
      if (!user) return;

      resolveDragConflict(conflictId, resolution, user.id);
      logger.info("Conflict resolved", { conflictId, resolution });
    },
    [resolveDragConflict, user],
  );

  // ─── Request sync ────────────────────────────────────────────────────────

  const requestSync = useCallback(() => {
    if (!bridge.isCollaborationActive) return;
    socketClient.requestSync();
  }, [bridge.isCollaborationActive]);

  // ─── Get collaboration status (from useIntegratedCollaboration) ──────────

  const getCollaborationStatus = useCallback((): CollaborationStatus => {
    const socketStats = socketClient.getStats();

    return {
      isActive: bridge.isCollaborationActive,
      connectionState: socketStats.connectionState,
      operationsPending: bridge.pendingOperations.size,
      lastSync: collaboration.lastSyncTime,
    };
  }, [bridge, collaboration.lastSyncTime]);

  // ─── Handle remote operation (from original useCollaboration) ────────────

  const handleRemoteOperation = useCallback(
    (operation: Operation) => {
      switch (operation.type) {
        case OperationType.CREATE_COMPONENT:
          if (operation.componentData) {
            const componentInput = parseComponentInput(operation.componentData);
            if (!componentInput) {
              logger.warn(
                "Ignoring invalid create component operation payload",
                {
                  operationId: operation.id,
                },
              );
              break;
            }
            addComponent(componentInput);
          }
          break;
        case OperationType.UPDATE:
          if (
            operation.componentId &&
            operation.field &&
            operation.value !== undefined
          ) {
            updateComponent(operation.componentId, {
              [operation.field]: operation.value,
            });
          }
          break;
        case OperationType.DELETE_COMPONENT:
          if (operation.componentId) {
            deleteComponent(operation.componentId);
          }
          break;
        case OperationType.MOVE:
          if (operation.componentId && operation.value) {
            const position = parsePosition(operation.value);
            if (!position) {
              logger.warn("Ignoring invalid move operation payload", {
                operationId: operation.id,
                componentId: operation.componentId,
              });
              break;
            }
            updateComponent(operation.componentId, { position });
          }
          break;
        default:
          logger.warn("Unknown operation type:", operation.type);
      }
    },
    [addComponent, updateComponent, deleteComponent],
  );

  // ─── Handle operation acknowledgment ─────────────────────────────────────

  const handleOperationAck = useCallback(
    (operationId: string, transformedOperation?: Operation) => {
      const timeoutRef = operationTimeoutRefs.current.get(operationId);
      if (timeoutRef) {
        clearTimeout(timeoutRef);
        operationTimeoutRefs.current.delete(operationId);
      }

      setOptimisticOperations((prev) => {
        const newMap = new Map(prev);
        newMap.delete(operationId);
        return newMap;
      });

      acknowledgeOperation(operationId, transformedOperation);

      if (transformedOperation) {
        handleRemoteOperation(transformedOperation);
      }

      logger.debug("Operation acknowledged", { operationId });
    },
    [acknowledgeOperation, handleRemoteOperation],
  );

  // ─── Apply optimistic operation ──────────────────────────────────────────

  const applyOptimisticOperation = useCallback(
    (operation: Operation) => {
      handleRemoteOperation(operation);
    },
    [handleRemoteOperation],
  );

  // ─── Create reverse operation ────────────────────────────────────────────

  const createReverseOperation = useCallback(
    (operation: Operation): Operation | null => {
      switch (operation.type) {
        case OperationType.CREATE_COMPONENT:
          return {
            ...operation,
            type: OperationType.DELETE_COMPONENT,
            value: undefined,
            componentData: undefined,
          };
        case OperationType.DELETE_COMPONENT:
          return {
            ...operation,
            type: OperationType.CREATE_COMPONENT,
            componentData: operation.oldValue,
          };
        case OperationType.UPDATE:
          return {
            ...operation,
            value: operation.oldValue,
            oldValue: operation.value,
          };
        case OperationType.MOVE:
          return {
            ...operation,
            value: operation.oldValue,
            oldValue: operation.value,
          };
        default:
          return null;
      }
    },
    [],
  );

  // ─── Revert optimistic operation ─────────────────────────────────────────

  const revertOptimisticOperation = useCallback(
    (operationId: string) => {
      const optimisticOp = optimisticOperations.get(operationId);
      if (!optimisticOp) return;

      const reverseOp = createReverseOperation(optimisticOp);
      if (reverseOp) {
        handleRemoteOperation(reverseOp);
      }

      setOptimisticOperations((prev) => {
        const newMap = new Map(prev);
        newMap.delete(operationId);
        return newMap;
      });

      const timeoutRef = operationTimeoutRefs.current.get(operationId);
      if (timeoutRef) {
        clearTimeout(timeoutRef);
        operationTimeoutRefs.current.delete(operationId);
      }

      failOperation(operationId);
      logger.warn("Optimistic operation reverted", { operationId });
    },
    [
      optimisticOperations,
      createReverseOperation,
      handleRemoteOperation,
      failOperation,
    ],
  );

  // ─── Send operation with optimistic updates (from original useCollaboration) ─

  const sendOperation = useCallback(
    async (operation: Operation, optimistic: boolean = true): Promise<void> => {
      if (!isActive || !user) {
        throw new Error("Collaboration not active");
      }

      const vectorClock = incrementVectorClock(user.id);
      const opWithVector = { ...operation, vector: vectorClock };

      // Apply optimistic update
      if (optimistic) {
        setOptimisticOperations((prev) =>
          new Map(prev).set(operation.id, opWithVector),
        );
        applyOptimisticOperation(opWithVector);

        // Set timeout to revert if not acknowledged
        const timeoutRef = setTimeout(() => {
          revertOptimisticOperation(operation.id);
        }, 5000);

        operationTimeoutRefs.current.set(operation.id, timeoutRef);
      }

      wsSendOperation(opWithVector);
      addPendingOperation(opWithVector);

      logger.debug("Operation sent", { operationId: operation.id, optimistic });
    },
    [
      isActive,
      user,
      incrementVectorClock,
      applyOptimisticOperation,
      revertOptimisticOperation,
      wsSendOperation,
      addPendingOperation,
    ],
  );

  // ─── Initialize collaboration event handlers via useWebSocket ────────────
  // These handle the OT operation routing through the useWebSocket abstraction.

  useEffect(() => {
    if (!wsIsConnected) return;

    const unsubscribe = [
      onProjectStateReceived((data) => {
        logger.info("Project state received", {
          projectId: data.project.id,
          componentCount: data.components.length,
          activeUserCount: data.session?.activeUsers.length ?? 0,
        });
        updateLastSyncTime();

        if (data.components) {
          // Apply server state to editor — handled by editor store integration
        }
      }),

      onUserJoined((data) => {
        if (!user || data.userId === user.id) return;

        addUser({
          userId: data.userId,
          user: data.user,
          currentAction: "idle",
          color: generateUserColor(data.userId),
          lastActivity: new Date(),
          isOnline: true,
        });

        logger.info("User joined collaboration", { userId: data.userId });
      }),

      onUserLeft((data) => {
        if (!user || data.userId === user.id) return;

        removeUser(data.userId);
        logger.info("User left collaboration", { userId: data.userId });
      }),

      onOperationReceived((data) => {
        if (!user || data.userId === user.id) return;

        handleRemoteOperation(data.payload.operation);
        logger.debug("Remote operation received", {
          operationId: data.payload.operation.id,
          type: data.payload.operation.type,
          componentId: data.payload.operation.componentId,
          userId: data.userId,
        });
      }),

      onOperationAck((data) => {
        handleOperationAck(data.operationId, data.transformedOperation);
      }),

      onCursorUpdate((data) => {
        if (!user || data.userId === user.id) return;

        updateUserCursor(data.userId, data.payload.position);
      }),

      onSelectionUpdate((data) => {
        if (!user || data.userId === user.id) return;

        updateUserSelection(data.userId, data.payload.selection);
      }),

      onPresenceUpdate((data) => {
        if (!user || data.userId === user.id) return;

        updateUserPresence(
          data.userId,
          data.payload.action === "disconnect" ? "offline" : "online",
        );
      }),
    ];

    return () => {
      unsubscribe.forEach((fn) => {
        if (typeof fn === "function") {
          fn();
        }
      });
    };
  }, [
    wsIsConnected,
    user,
    addUser,
    handleOperationAck,
    handleRemoteOperation,
    onCursorUpdate,
    onOperationAck,
    onOperationReceived,
    onPresenceUpdate,
    onProjectStateReceived,
    onSelectionUpdate,
    onUserJoined,
    onUserLeft,
    removeUser,
    updateLastSyncTime,
    updateUserCursor,
    updateUserPresence,
    updateUserSelection,
  ]);

  // ─── Auto-reconnect on connection loss (from useIntegratedCollaboration) ─

  useEffect(() => {
    if (!bridge.isCollaborationActive) return;

    const handleDisconnection = (reason: string) => {
      logger.warn("Collaboration connection lost", { reason });

      if (
        isWebSocketUnavailable.current ||
        isRetrying.current ||
        reason === "io server disconnect"
      ) {
        logger.info("Skipping auto-reconnect", {
          reason,
          isRetrying: isRetrying.current,
          isWebSocketUnavailable: isWebSocketUnavailable.current,
        });
        return;
      }

      if (connectionRetryCount.current >= maxRetries) {
        logger.warn(
          "Max reconnection attempts reached, disabling collaboration",
        );
        isWebSocketUnavailable.current = true;
        lastFailureTime.current = Date.now();
        return;
      }

      // Exponential backoff
      const backoffDelay = Math.min(
        2000 * Math.pow(2, connectionRetryCount.current),
        30000,
      );

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        if (
          bridge.isCollaborationActive &&
          !isRetrying.current &&
          !isWebSocketUnavailable.current
        ) {
          reconnect().catch((error) => {
            logger.error("Auto-reconnect failed", error);
          });
        }
      }, backoffDelay);
    };

    const handleReconnection = () => {
      logger.info("Collaboration connection restored");
      requestSync();
    };

    const unsubscribeDisconnected = socketClient.on(
      "disconnected",
      handleDisconnection,
    );
    const unsubscribeReconnected = socketClient.on(
      "reconnected",
      handleReconnection,
    );

    return () => {
      unsubscribeDisconnected();
      unsubscribeReconnected();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [bridge.isCollaborationActive, reconnect, requestSync]);

  // ─── Periodic sync check (from useIntegratedCollaboration) ───────────────

  useEffect(() => {
    if (!bridge.isCollaborationActive) return;

    const syncInterval = setInterval(() => {
      if (collaboration.needsSync()) {
        logger.info("Requesting periodic sync");
        requestSync();
      }

      bridge.retryFailedOperations();
      bridge.clearOldOperations();
    }, 30000);

    return () => clearInterval(syncInterval);
  }, [bridge, collaboration, requestSync]);

  // ─── Auto-cleanup on unmount ─────────────────────────────────────────────

  useEffect(() => {
    const operationTimeouts = operationTimeoutRefs.current;

    return () => {
      // Clear cursor update timer
      if (cursorUpdateTimer.current) {
        clearTimeout(cursorUpdateTimer.current);
      }

      // Clear optimistic operation timeouts
      operationTimeouts.forEach((timeout) => clearTimeout(timeout));
      operationTimeouts.clear();

      // Clear pending retry timeouts
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      // Reset retry state and circuit breaker
      isRetrying.current = false;
      connectionRetryCount.current = 0;
      isWebSocketUnavailable.current = false;
      isJoining.current = false;
      currentJoinProjectId.current = null;

      // Stop collaboration if active
      if (bridgeActiveRef.current) {
        stopCollaboration().catch((error) => {
          logger.error("Error stopping collaboration on unmount", error);
        });
      }
    };
  }, [stopCollaboration]);

  // ─── Assemble return value ───────────────────────────────────────────────

  const state: CollaborationState = {
    isActive: bridge.isCollaborationActive || isActive,
    isConnected: socketClient.isConnected(),
    activeUsers: collaboration.getActiveUsers(),
    currentUser: collaboration.getCurrentUser(),
    conflicts: Array.from(dragConflicts.values()),
    hasActiveConflicts: hasActiveConflicts(),
    stats: collaboration.getStats(),
  };

  const actions: CollaborationActions = {
    startCollaboration,
    stopCollaboration,
    reconnect,
    sendOperation,
    updateCursor,
    sendCursorUpdate: sendCursorUpdateDirect,
    updateSelection,
    resolveConflict,
    requestSync,
    getCollaborationStatus,
  };

  return { ...state, ...actions };
};
